/**
 * Persistence — sessionStorage/localStorage helpers for task state.
 *
 * Debounced writes (2s) replace the old useEffect that wrote 50x/sec during streaming.
 */

import type { TaskState, SerializedTaskState } from "./types";
import { toCanonicalPath } from "@/lib/file-paths";

const TASK_ID_STORAGE_KEY = "bash:taskId";
const TASKS_STORAGE_KEY = "bash:tasks";
const STORAGE_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours

// =============================================================================
// localStorage — selectedTaskId
// =============================================================================

export function getStoredTaskId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return localStorage.getItem(TASK_ID_STORAGE_KEY);
  } catch (e) {
    console.warn("[Persistence] getStoredTaskId failed:", e);
    return null;
  }
}

export function setStoredTaskId(taskId: string | null): void {
  if (typeof window === "undefined") return;
  try {
    if (taskId) {
      localStorage.setItem(TASK_ID_STORAGE_KEY, taskId);
    } else {
      localStorage.removeItem(TASK_ID_STORAGE_KEY);
    }
  } catch (e) {
    console.warn("[Persistence] setStoredTaskId failed:", e);
  }
}

// =============================================================================
// sessionStorage — tasks Map
// =============================================================================

export function getStoredTasks(): Map<string, TaskState> | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = sessionStorage.getItem(TASKS_STORAGE_KEY);
    if (!stored) return null;
    const parsed: Record<string, SerializedTaskState> = JSON.parse(stored);
    const tasks = new Map<string, TaskState>();
    for (const [taskId, s] of Object.entries(parsed)) {
      tasks.set(taskId, {
        taskId: s.taskId,
        messages: s.messages.map((m) => ({
          ...m,
          timestamp: new Date(m.timestamp),
        })),
        streamingMessage: null,
        phase: "idle",
        title: s.title,
        createdAt: new Date(s.createdAt),
        lastActivityAt: new Date(s.lastActivityAt),
        agentSlug: s.agentSlug,
        // Locked engine — defaults to claude for rows persisted before this
        // field existed; re-hydrated from the server on the next task open.
        runtime: s.runtime ?? "native",
        // Pinned model id (§4.7) — undefined for legacy rows persisted before
        // this field existed; re-hydrated from the server on the next task open.
        model: s.model,
        pendingInteraction: null,
        pendingQueue: [],
        pendingByLane: {},
        tokenUsage: s.tokenUsage ?? { input: 0, output: 0 },
        ...(s.contextUsage ? { contextUsage: s.contextUsage } : {}),
        turnSummary: null,
        turnStartTime: null,
        agentStartedAt: null,
        // Canonicalise on rehydrate. Cached `outputs` written before the
        // canonical-path rollout hold absolute sandbox paths
        // (e.g. `/app/workspace/files/foo.md`). `syncHandlers.ts`
        // prefers a non-empty cached value over fresh server data, so
        // without this map the first session after deploy keeps stale
        // absolute paths and lookups in ToolUseBlock / FileQuickLookCard
        // silently miss.
        outputs: (s.outputs ?? []).map((o) =>
          o.filePath ? { ...o, filePath: toCanonicalPath(o.filePath) } : o
        ),
        todos: s.todos || [],
        promptSuggestion: s.promptSuggestion ?? null,
      });
    }
    return tasks;
  } catch (e) {
    console.warn("[Persistence] getStoredTasks failed:", e);
    return null;
  }
}

export function setStoredTasks(tasks: Map<string, TaskState>): void {
  if (typeof window === "undefined") return;
  try {
    const serialized: Record<string, SerializedTaskState> = {};
    for (const [taskId, task] of tasks) {
      // Skip temp tasks (optimistic, pending server ack)
      if (taskId.startsWith("corr-")) continue;
      // P5.4 Agent Teams — hidden teammate lanes are never persisted: the
      // serialized shape drops teammateMeta, so a restored copy would lose its
      // lane identity and leak into task lists. Lanes rebuild from the parent's
      // delegated markers + a MESSAGE_SYNC fetch instead.
      if (task.teammateMeta) continue;
      // RENDER_FROM_TRUTH — the overlay never persists: flattened, it becomes
      // a client-only assistant row that can't content-match its server row on
      // the next sync and duplicates it. The first MESSAGE_SYNC after
      // rehydrate re-delivers the turn from the DB instead.
      const messages = task.messages;
      if (messages.length === 0) continue;
      serialized[taskId] = {
        taskId: task.taskId,
        messages: messages.map((m) => ({
          ...m,
          timestamp: m.timestamp.toISOString(),
        })),
        phase: "idle",
        title: task.title,
        createdAt: task.createdAt.toISOString(),
        lastActivityAt: task.lastActivityAt.toISOString(),
        agentSlug: task.agentSlug,
        runtime: task.runtime,
        model: task.model,
        outputs: task.outputs.length > 0 ? task.outputs : undefined,
        todos: task.todos.length > 0 ? task.todos : undefined,
        promptSuggestion: task.promptSuggestion ?? undefined,
        tokenUsage:
          task.tokenUsage.input > 0 || task.tokenUsage.output > 0
            ? task.tokenUsage
            : undefined,
        contextUsage: task.contextUsage,
      };
    }
    sessionStorage.setItem(TASKS_STORAGE_KEY, JSON.stringify(serialized));
  } catch (e) {
    if (e instanceof DOMException && e.name === "QuotaExceededError") {
      console.warn(
        "[Persistence] Storage quota exceeded — task state may not persist across refresh"
      );
    } else {
      console.warn("[Persistence] setStoredTasks failed:", e);
    }
  }
}

// =============================================================================
// Debounced persistence (2s instead of every render)
// =============================================================================

let persistTimer: ReturnType<typeof setTimeout> | null = null;

export function schedulePersist(tasks: Map<string, TaskState>): void {
  if (persistTimer) clearTimeout(persistTimer);
  persistTimer = setTimeout(() => {
    persistTimer = null;
    setStoredTasks(tasks);
  }, 2000);
}

export function flushPersist(tasks: Map<string, TaskState>): void {
  if (persistTimer) {
    clearTimeout(persistTimer);
    persistTimer = null;
  }
  setStoredTasks(tasks);
}

// =============================================================================
// Stale entry cleanup (run once on mount)
// =============================================================================

export function cleanupStaleEntries(): void {
  if (typeof window === "undefined") return;
  try {
    const stored = sessionStorage.getItem(TASKS_STORAGE_KEY);
    if (!stored) return;
    const parsed: Record<string, SerializedTaskState> = JSON.parse(stored);
    const now = Date.now();
    let changed = false;
    for (const [taskId, task] of Object.entries(parsed)) {
      if (now - new Date(task.lastActivityAt).getTime() > STORAGE_MAX_AGE_MS) {
        delete parsed[taskId];
        changed = true;
      }
    }
    if (changed) {
      sessionStorage.setItem(TASKS_STORAGE_KEY, JSON.stringify(parsed));
    }
  } catch (e) {
    console.warn("[Persistence] cleanupStaleEntries failed:", e);
  }
}

// =============================================================================
// LRU eviction helpers
// =============================================================================

const MAX_TASKS_IN_MEMORY = 20;

export function evictLRU(
  tasks: Map<string, TaskState>,
  selectedTaskId: string | null
): Map<string, TaskState> {
  if (tasks.size <= MAX_TASKS_IN_MEMORY) return tasks;

  const sorted = Array.from(tasks.entries()).sort(
    ([, a], [, b]) => b.lastActivityAt.getTime() - a.lastActivityAt.getTime()
  );

  const keep = new Set<string>();
  if (selectedTaskId) keep.add(selectedTaskId);

  for (const [taskId] of sorted) {
    if (keep.size >= MAX_TASKS_IN_MEMORY) break;
    keep.add(taskId);
  }

  const next = new Map<string, TaskState>();
  for (const [taskId, task] of tasks) {
    if (keep.has(taskId)) {
      next.set(taskId, task);
    }
  }
  return next;
}
